import IntranetLayout from "@/components/IntranetLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Leaf, TrendingDown, Fuel, Ship, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from "recharts";

const emissionsData = [
  { month: "Oct", crewliner1: 42, crewliner2: 38, combi1: 55 },
  { month: "Nov", crewliner1: 40, crewliner2: 36, combi1: 52 },
  { month: "Dec", crewliner1: 35, crewliner2: 33, combi1: 48 },
  { month: "Jan", crewliner1: 38, crewliner2: 34, combi1: 50 },
  { month: "Feb", crewliner1: 36, crewliner2: 32, combi1: 47 },
  { month: "Mar", crewliner1: 34, crewliner2: 30, combi1: 45 },
];

const fuelData = [
  { vessel: "MV Crewliner I", fuelUsed: "12,400 L", efficiency: "3.2 L/nm", co2: "34 tCO₂", trend: "-5.2%" },
  { vessel: "MV Crewliner II", fuelUsed: "10,800 L", efficiency: "2.9 L/nm", co2: "30 tCO₂", trend: "-6.1%" },
  { vessel: "MV Combi I", fuelUsed: "18,200 L", efficiency: "4.1 L/nm", co2: "45 tCO₂", trend: "-3.8%" },
];

const EmployeeESGEmissions = () => {
  return (
    <IntranetLayout>
      <div className="mb-6">
        <h1 className="font-display text-2xl text-foreground">ESG & Emissions</h1>
        <p className="text-sm text-muted-foreground font-body mt-1">Track environmental impact, fuel efficiency, and sustainability metrics</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total CO₂ MTD", value: "109 t", icon: Leaf, color: "text-emerald-600" },
          { label: "Fuel Consumed", value: "41.4K L", icon: Fuel, color: "text-amber-500" },
          { label: "Avg Efficiency", value: "3.4 L/nm", icon: BarChart3, color: "text-teal" },
          { label: "YoY Reduction", value: "-5.0%", icon: TrendingDown, color: "text-lime-dark" },
        ].map(k => (
          <Card key={k.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <k.icon className={`w-8 h-8 ${k.color}`} />
              <div>
                <p className="text-xl font-display text-foreground">{k.value}</p>
                <p className="text-[11px] text-muted-foreground font-body">{k.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="emissions">
        <TabsList>
          <TabsTrigger value="emissions">Emissions Trend</TabsTrigger>
          <TabsTrigger value="fuel">Fuel Tracker</TabsTrigger>
        </TabsList>

        <TabsContent value="emissions" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">CO₂ Emissions by Vessel (tCO₂/month)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={emissionsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="crewliner1" stroke="hsl(var(--teal))" name="Crewliner I" strokeWidth={2} />
                  <Line type="monotone" dataKey="crewliner2" stroke="hsl(var(--lime))" name="Crewliner II" strokeWidth={2} />
                  <Line type="monotone" dataKey="combi1" stroke="#f59e0b" name="Combi I" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fuel" className="mt-4 space-y-3">
          {fuelData.map(f => (
            <Card key={f.vessel} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Ship className="w-6 h-6 text-teal" />
                    <h3 className="font-display text-base text-foreground">{f.vessel}</h3>
                  </div>
                  <span className="text-sm font-display text-emerald-600">{f.trend}</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  {[
                    { label: "Fuel Used", value: f.fuelUsed },
                    { label: "Efficiency", value: f.efficiency },
                    { label: "CO₂", value: f.co2 },
                  ].map(s => (
                    <div key={s.label}>
                      <p className="font-display text-sm text-foreground">{s.value}</p>
                      <p className="text-[10px] text-muted-foreground font-body">{s.label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </IntranetLayout>
  );
};

export default EmployeeESGEmissions;
